import React, {useCallback, useEffect} from "react";
import {
    Box,
    Button,
    Icon,
    Text,
    TextLine,
    View,
    useLocalStorage,
    usePatronTheme,
} from "@unibuddy/patron";
import styled, {keyframes} from "styled-components";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import {IconClose} from "../../icons";
import {usePostMessage} from "../../hooks/usePostMessage";
import {DISABLE_CTA_ACTIONS_ON_MOBILE} from "../../utils/featureFlags";
import {useScreensizeContext} from "../../providers/ScreensizeProvider";
import {CTAActionsScreenReaderLabel} from "./CTAActionsScreenReaderLabel";

export const ASSISTANT_SHOULD_SHOW_ACTIONS_KEY = "ASSISTANT_SHOULD_SHOW_ACTIONS";
const IFRAME_SHOW_DELAY = 6000; // ms

const message = keyframes`
    0% {
       max-height: 100vmax;
    }
    100% {
       transform: scale(1);
       max-height: 100vmax;
       overflow: visible;
       padding-top: 16px;
    }
`;

const Container = styled(Box)`
    box-sizing: border-box;
`;

const MessageContainer = styled(Box)`
    transform: scale(0);
    text-align: right;
    align-self: flex-end;
    transform-origin: 100% 100%;
    animation: ${message} 0.15s ease-out 0s forwards;
    animation-delay: 0s;
`;

const DelayedMessageContainer = styled(MessageContainer)`
    animation-delay: 3s;
`;

const BoxWithShadow = styled(Box)`
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    &:hover {
        cursor: pointer;
    }
`;

export function AppCTAActions() {
    const theme = usePatronTheme();

    const {isSmallDeviceDimension, isSmallInnerHeight} = useScreensizeContext();
    const shouldDisableActionsOnMobile = useBooleanFeatureFlag(
        DISABLE_CTA_ACTIONS_ON_MOBILE.key,
        DISABLE_CTA_ACTIONS_ON_MOBILE.defaultValue,
    );
    const urlParams = new URLSearchParams(window.location.search);
    const universityId = urlParams.get("universityId") ?? "";
    const {postMessage} = usePostMessage();
    const [shouldShowActions, setShouldShowActions] = useLocalStorage(
        `${ASSISTANT_SHOULD_SHOW_ACTIONS_KEY}-${universityId}`,
        true,
    ) as [boolean, (b: boolean) => void];

    useEffect(() => {
        const messageListener = (e: MessageEvent) => {
            if (e.data === "showCTAActions=false") {
                setShouldShowActions(false);
            }
        };

        window.addEventListener("message", messageListener, true);

        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, [setShouldShowActions]);

    useEffect(() => {
        if (shouldShowActions) {
            const delayIframe = setTimeout(() => {
                postMessage("showCTAActions=true");
            }, IFRAME_SHOW_DELAY);
            return () => clearTimeout(delayIframe);
        }
    }, [shouldShowActions, postMessage]);

    useEffect(() => {
        if (isSmallInnerHeight) {
            postMessage("showCTAActions=false");
        } else if (isSmallDeviceDimension) {
            const showCTAActions = !shouldDisableActionsOnMobile && shouldShowActions;
            const showCTAActionsString = showCTAActions ? "true" : "false";
            postMessage(`showCTAActions=${showCTAActionsString}`);
        }
    }, [
        isSmallDeviceDimension,
        shouldDisableActionsOnMobile,
        shouldShowActions,
        postMessage,
        isSmallInnerHeight,
    ]);

    const handleCloseClicked = useCallback(() => {
        setShouldShowActions(false);
        postMessage("showCTAActions=false");
    }, [setShouldShowActions, postMessage]);

    const handleActionClicked = useCallback(() => {
        handleCloseClicked();
        postMessage("chatVisible=true");
    }, [handleCloseClicked, postMessage]);

    if (!shouldShowActions) return null;

    if (
        (isSmallDeviceDimension && shouldDisableActionsOnMobile) ||
        (isSmallInnerHeight && shouldDisableActionsOnMobile)
    )
        return null;

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            handleActionClicked();
        }
    };

    return (
        <Container
            display="flex"
            flexDirection="column-reverse"
            // height must match value in embed.js
            height="250px"
            position="relative"
            overflow="hidden"
        >
            <CTAActionsScreenReaderLabel />
            <View padding="medium-large">
                <MessageContainer
                    display="flex"
                    flexDirection="row-reverse"
                    alignItems="flex-end"
                    maxH="0"
                    overflow="hidden"
                >
                    <Box>
                        <BoxWithShadow
                            position="absolute"
                            // Normally we'd avoid negative margins, but this is necessary
                            // for the animation to flow correctly. -24px is because we want
                            // 8px space between the button and the top action + 16px to account
                            // for half the height of the button
                            top="-24px"
                            right="0"
                            borderRadius="full"
                        >
                            <Button
                                iconOnly
                                round
                                size="sm"
                                color="primary"
                                onClick={handleCloseClicked}
                                aria-label="Close actions"
                            >
                                <Icon icon={IconClose} size="small" />
                            </Button>
                        </BoxWithShadow>
                    </Box>
                    <BoxWithShadow
                        role="button"
                        padding="small-medium"
                        borderRadius="xsmall"
                        onClick={handleActionClicked}
                        _hover={{bgColor: "grey100"}}
                        bgColor="white"
                        flex="0 1 auto"
                        tabIndex="0"
                        onKeyDown={onKeyDown}
                    >
                        <Text weight="bold">Hi there! 👋 Looking for something?</Text>
                    </BoxWithShadow>
                </MessageContainer>
                <DelayedMessageContainer
                    display="flex"
                    flexDirection="row-reverse"
                    alignItems="flex-end"
                    maxH="0"
                    overflow="hidden"
                >
                    <BoxWithShadow
                        role="button"
                        padding="small-medium"
                        borderRadius="xsmall"
                        onClick={handleActionClicked}
                        bgColor="grey50"
                        _hover={{bgColor: theme.colors.primaryColor, color: "white"}}
                        flex="0 1 auto"
                        tabIndex="0"
                        onKeyDown={onKeyDown}
                    >
                        <Text color="inherit">
                            <TextLine weight="bold" color="inherit">
                                Direct me
                            </TextLine>{" "}
                            to the right information ℹ️
                        </Text>
                    </BoxWithShadow>
                </DelayedMessageContainer>
                <DelayedMessageContainer
                    display="flex"
                    flexDirection="row-reverse"
                    alignItems="flex-end"
                    maxH="0"
                    overflow="hidden"
                >
                    <BoxWithShadow
                        role="button"
                        padding="small-medium"
                        borderRadius="xsmall"
                        onClick={handleActionClicked}
                        bgColor="grey50"
                        _hover={{bgColor: theme.colors.primaryColor, color: "white"}}
                        flex="0 1 auto"
                        tabIndex="0"
                        onKeyDown={onKeyDown}
                    >
                        <Text color="currentColor">
                            <TextLine weight="bold" color="currentColor">
                                Connect me
                            </TextLine>{" "}
                            to the right person 👩‍🎓
                        </Text>
                    </BoxWithShadow>
                </DelayedMessageContainer>
            </View>
        </Container>
    );
}
